import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import { MapPin, Heart, MessageCircle, Pencil, Flag, Camera, Check, Ban, Shield, ShieldAlert, BadgeCheck, Lock, Sparkles } from "lucide-react";
import api, { fileUrl, apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { THEMES, TAGS } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import ProfileJar from "../components/ProfileJar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import PostComposer from "../components/PostComposer";
import PostCard from "../components/PostCard";
import TipModal from "../components/TipModal";
import ReportDialog from "../components/ReportDialog";
import AlbumsTab from "../components/AlbumsTab";
import SupporterWall from "../components/SupporterWall";
import TransactionCenter from "../components/TransactionCenter";
import ImageLightbox from "../components/ImageLightbox";
import VerifiedBadge, { TrustedBadge, UnderWingBadge } from "../components/VerifiedBadge";
import ShareJar from "../components/ShareJar";
import { UserPlus, UserCheck } from "lucide-react";
import { toast } from "sonner";

export default function Profile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [tipOpen, setTipOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [report, setReport] = useState(null);
  const [follow, setFollow] = useState({ following: false, followers: 0, following_count: 0 });
  const [blocked, setBlocked] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [verifStatus, setVerifStatus] = useState(null);
  const [verifOpen, setVerifOpen] = useState(false);
  const [verifNote, setVerifNote] = useState("");
  const [verifSections, setVerifSections] = useState([]);
  const [verifDocs, setVerifDocs] = useState({});
  const [uploadingSec, setUploadingSec] = useState(null);
  const [wantProtected, setWantProtected] = useState(false);
  const [protectedReason, setProtectedReason] = useState("");
  const celebrated = useRef(false);
  const isOwn = user && user.user_id === userId;
  const isMod = user && !isOwn && (user.role === "admin" || user.is_guardian);

  const load = useCallback(async () => {
    const [p, ps, fs] = await Promise.all([
      api.get(`/profiles/${userId}`),
      api.get(`/posts/user/${userId}`),
      api.get(`/follow/status/${userId}`),
    ]);
    setProfile(p.data); setPosts(ps.data); setFollow(fs.data);
    setBlocked(!!p.data.blocked_by_me);
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (isOwn) api.get("/verification/my-status").then((r) => setVerifStatus(r.data.status)).catch(() => {});
  }, [isOwn, userId]);

  const openVerif = () => {
    setVerifOpen(true);
    api.get("/verification/sections").then((r) => setVerifSections(r.data)).catch(() => {});
  };

  const uploadDoc = async (sec, file) => {
    if (!file) return;
    setUploadingSec(sec.id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setVerifDocs((d) => ({ ...d, [sec.id]: { url: data.url, name: file.name } }));
    } catch (e) { toast.error(apiErr(e)); }
    setUploadingSec(null);
  };

  const submitVerif = async () => {
    const missing = verifSections.find((s) => s.required && !verifDocs[s.id]);
    if (missing) { toast.error(`Please upload: ${missing.label}`); return; }
    try {
      const documents = Object.entries(verifDocs).map(([section_id, v]) => ({ section_id, url: v.url }));
      await api.post("/verification/request", { note: verifNote, documents, want_protected: wantProtected, protected_reason: protectedReason });
      setVerifStatus("pending");
      setVerifOpen(false);
      setVerifNote("");
      setVerifDocs({});
      toast.success("Request sent — our team will review it soon 💛");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const toggleBlock = async () => {
    try {
      if (blocked) { await api.delete(`/block/${userId}`); setBlocked(false); toast.success("Unblocked"); }
      else { await api.post(`/block/${userId}`); setBlocked(true); toast.success("Blocked. They can no longer message or interact with you."); }
    } catch (e) { toast.error(apiErr(e)); }
  };
  const suspendThisUser = async () => {
    if (!window.confirm("Put this member on a 24-hour cool-down while our team reviews? Misusing this power will remove your own account.")) return;
    try { await api.post(`/moderate/suspend/${userId}`, { hours: 24, reason: "Guardian cool-down (profile)" }); toast.success("Member suspended for 24h. Admins will review. 🛡️"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const toggleGuardian = async () => {
    try {
      if (profile.is_guardian) { await api.delete(`/admin/guardian/${userId}`); toast.success("Guardian access removed"); }
      else { await api.post(`/admin/guardian/${userId}`); toast.success("They're now a Community Guardian 🛡️"); }
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const toggleVerify = async () => {
    try {
      if (profile.verified) { await api.delete(`/admin/verify/${userId}`); toast.success("Verification removed"); }
      else { await api.post(`/admin/verify/${userId}`); toast.success("Member is now Verified ✅"); }
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const applyGuardian = async () => {
    const reason = window.prompt("Why would you like to be a Community Guardian? (Tell our team a little about you)");
    if (reason === null) return;
    try { await api.post("/guardian/apply", { reason }); toast.success("Application sent 🛡️ Our team will review it soon. Thank you!"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  useEffect(() => {
    if (!profile || celebrated.current) return;
    const reached = profile.goal_enabled && profile.goal_amount > 0 && (profile.goal_raised || 0) >= profile.goal_amount;
    if (reached) {
      celebrated.current = true;
      const colors = ["#E87FA3", "#86C79A", "#F2CE63", "#C6ADEA", "#F2A98B"];
      confetti({ particleCount: 140, spread: 80, origin: { y: 0.35 }, colors });
      setTimeout(() => confetti({ particleCount: 90, spread: 100, origin: { y: 0.4 }, colors }), 350);
    }
  }, [profile]);

  const toggleFollow = async () => {
    if (!user) { toast.error("Sign in to follow."); return; }
    try {
      const { data } = await api.post(`/${follow.following ? "unfollow" : "follow"}/${userId}`);
      setFollow((f) => ({ ...f, following: data.following, followers: f.followers + (data.following ? 1 : -1) }));
      toast.success(data.following ? "Following 💛 Their updates will appear first in your timeline." : "Unfollowed");
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!profile) return <div className="min-h-[60vh] flex items-center justify-center text-copper font-serif">Loading…</div>;
  if (profile.private) return (
    <div data-testid="private-profile" className="max-w-md mx-auto px-4 py-24 text-center">
      <div className="w-16 h-16 rounded-full bg-secondary mx-auto flex items-center justify-center text-copper"><Heart className="w-7 h-7" /></div>
      <h1 className="font-serif text-2xl font-bold text-bronze mt-4">This profile is private</h1>
      <p className="text-muted-foreground mt-2">This member is here to quietly support others and has chosen to stay private. 💛</p>
    </div>
  );
  const theme = THEMES[profile.theme] || THEMES.copper;
  const isSupporter = profile.account_type === "supporter";

  return (
    <div data-testid="user-profile-view" className="max-w-4xl mx-auto px-4 py-6">
      <div className="bg-card rounded-3xl border border-border overflow-hidden shadow-sm">
        <div className={`h-40 sm:h-56 ${theme.cover} relative`}>
          {profile.cover_url && (
            <img src={fileUrl(profile.cover_url)} alt="" data-testid="profile-cover"
              onClick={() => setLightbox({ src: profile.cover_url, alt: `${profile.name}'s cover` })}
              className="w-full h-full object-cover cursor-zoom-in" />
          )}
        </div>
        <div className="px-4 sm:px-8 pb-6">
          <div className="flex flex-col items-center text-center gap-3 -mt-14">
            {profile.protected && !isOwn ? (
              <div data-testid="protected-avatar" className="relative z-10 w-28 h-28 rounded-full border-4 border-card bg-gradient-to-br from-penny/60 to-copper/60 shadow-md flex items-center justify-center">
                <Heart className="w-12 h-12 text-white" fill="currentColor" />
              </div>
            ) : (
              <img src={fileUrl(profile.avatar_url) || `https://api.dicebear.com/7.x/lorelei/svg?seed=${profile.user_id}`}
                alt={profile.name} data-testid="profile-avatar"
                onClick={() => profile.avatar_url && setLightbox({ src: profile.avatar_url, alt: profile.name })}
                className={`relative z-10 w-28 h-28 rounded-full object-cover object-center border-4 border-card bg-secondary shadow-md ${profile.avatar_url ? "cursor-zoom-in" : ""}`} />
            )}
            <div className="min-w-0 w-full flex flex-col items-center">
              <h1 className="font-serif text-2xl sm:text-3xl font-bold text-bronze inline-flex items-center justify-center gap-2">
                {profile.name}
                {profile.verified && !profile.trusted && <VerifiedBadge size="lg" />}
              </h1>
              {(profile.trusted || profile.protected) && (
                <div className="flex flex-wrap justify-center gap-1.5 mt-2">
                  {profile.trusted && <TrustedBadge />}
                  {profile.protected && <UnderWingBadge />}
                </div>
              )}
              <div className="flex items-center justify-center gap-3 text-sm text-muted-foreground mt-0.5 flex-wrap">
                {profile.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {profile.location}</span>}
                <span data-testid="followers-count"><b className="text-bronze">{follow.followers}</b> supporter{follow.followers === 1 ? "" : "s"}</span>
                {profile.seeking && <span className="bg-sage/20 text-sage px-2 py-0.5 rounded-full text-xs font-medium">Seeking support</span>}
                {profile.earned_today != null && (
                  <span data-testid="earned-today" className="bg-penny/20 text-bronze px-2 py-0.5 rounded-full text-xs font-medium">Earned today: ${profile.earned_today.toFixed(2)}</span>
                )}
              </div>
            </div>
            <div className="flex flex-wrap justify-center gap-2 w-full">
              {isOwn ? (
                <>
                  <Button data-testid="edit-profile-btn" onClick={() => setEditOpen(true)} variant="outline" className="rounded-full border-copper/40 text-copper hover:bg-copper/10">
                    <Pencil className="w-4 h-4 mr-1.5" /> Edit profile
                  </Button>
                  {!isSupporter && !profile.is_guardian && (
                    <Button data-testid="apply-guardian-btn" onClick={applyGuardian} variant="outline" className="rounded-full">
                      <Shield className="w-4 h-4 mr-1.5" /> Become a Guardian
                    </Button>
                  )}
                  {profile.is_guardian && (
                    <span data-testid="guardian-badge" className="inline-flex items-center gap-1 text-xs font-semibold text-copper bg-copper/10 rounded-full px-3 py-1.5"><Shield className="w-3.5 h-3.5" /> Guardian</span>
                  )}
                </>
              ) : (
                <>
                  {!isSupporter && (
                    <>
                      <Button data-testid="follow-btn" onClick={toggleFollow} variant={follow.following ? "outline" : "default"}
                        className={`rounded-full ${follow.following ? "border-copper/40 text-copper hover:bg-copper/10" : "bg-sage hover:bg-sage/90 text-white"}`}>
                        {follow.following ? <><UserCheck className="w-4 h-4 mr-1.5" /> Following</> : <><UserPlus className="w-4 h-4 mr-1.5" /> Follow</>}
                      </Button>
                      <Button data-testid="profile-tip-btn" onClick={() => setTipOpen(true)} className="bg-copper hover:bg-copper/90 text-white rounded-full">
                        <Heart className="w-4 h-4 mr-1.5" /> Tip
                      </Button>
                    </>
                  )}
                  {user && profile.accept_dms !== false && (
                    <Button data-testid="profile-message-btn" onClick={() => navigate(`/messages/${profile.user_id}`)} variant="outline" className="rounded-full">
                      <MessageCircle className="w-4 h-4" />
                    </Button>
                  )}
                  {user && (
                    <Button data-testid="profile-report-btn" onClick={() => setReport({ target_user_id: profile.user_id })} variant="ghost" className="rounded-full text-muted-foreground hover:text-destructive">
                      <Flag className="w-4 h-4" />
                    </Button>
                  )}
                  {user && (
                    <Button data-testid="block-btn" onClick={toggleBlock} variant="ghost" className="rounded-full text-muted-foreground hover:text-destructive">
                      {blocked ? <><UserCheck className="w-4 h-4 mr-1.5" /> Unblock</> : <><Ban className="w-4 h-4 mr-1.5" /> Block</>}
                    </Button>
                  )}
                  {isMod && (
                    <Button data-testid="profile-suspend-btn" onClick={suspendThisUser} variant="outline" className="rounded-full text-copper border-copper/40">
                      <ShieldAlert className="w-4 h-4 mr-1.5" /> Cool-down
                    </Button>
                  )}
                  {user?.role === "admin" && (
                    <Button data-testid="guardian-toggle-btn" onClick={toggleGuardian} variant="outline" className="rounded-full">
                      <Shield className="w-4 h-4 mr-1.5" /> {profile.is_guardian ? "Remove guardian" : "Make guardian"}
                    </Button>
                  )}
                  {user?.role === "admin" && (
                    <Button data-testid="verify-toggle-btn" onClick={toggleVerify} variant="outline"
                      className="rounded-full text-sage border-sage/40 hover:bg-sage/10">
                      <BadgeCheck className="w-4 h-4 mr-1.5" /> {profile.verified ? "Unverify" : "Verify"}
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>

          {isOwn && !isSupporter && !profile.verified && (
            <div data-testid="verify-cta" className="mt-4 bg-sage/10 border border-sage/25 rounded-2xl p-4 flex flex-col items-center text-center gap-2">
              <BadgeCheck className="w-6 h-6 text-sage" />
              {verifStatus === "pending" ? (
                <>
                  <p className="font-serif text-bronze font-medium">Verification pending review</p>
                  <p className="text-xs text-muted-foreground max-w-sm">Thanks for applying 💛 Our team is reviewing your request — you'll get a verified badge once approved.</p>
                </>
              ) : (
                <>
                  <p className="font-serif text-bronze font-medium">Get a Verified badge</p>
                  <p className="text-xs text-muted-foreground max-w-sm">Verified members earn extra trust from givers. Request a review and tell us a little about your situation.</p>
                  <Button data-testid="request-verify-btn" onClick={openVerif} className="rounded-full bg-sage hover:bg-sage/90 text-white mt-1">
                    <BadgeCheck className="w-4 h-4 mr-1.5" /> Request verification
                  </Button>
                </>
              )}
            </div>
          )}

          {profile.protected && (
            <div data-testid="under-wing-card" className="mt-5 bg-penny/10 border border-penny/30 rounded-2xl p-5 text-center">
              <div className="flex items-center justify-center gap-2 text-penny mb-2">
                <Heart className="w-5 h-5" fill="currentColor" />
                <span className="font-mono text-xs uppercase tracking-widest">Under Our Wing</span>
              </div>
              {profile.protected_blurb && (
                <p className="text-foreground/90 leading-relaxed max-w-xl mx-auto whitespace-pre-wrap font-serif text-lg">{profile.protected_blurb}</p>
              )}
              <p className="text-sm text-muted-foreground leading-relaxed max-w-xl mx-auto mt-3">
                This neighbour has quietly done everything we asked — sharing ID and proof of their situation privately with our team — to show they're real and truly in need. For deeply personal reasons, they've asked us to keep their name and face private, and we've honoured that. We've verified them ourselves: they are safe, real, and so very worthy of your kindness. 🤍
              </p>
              {isOwn && <p className="text-xs text-penny/80 mt-3">This is how the community sees you — your real name and photo stay private, just with us.</p>}
            </div>
          )}

          {!isSupporter && profile.need && (
            <div className="mt-5 bg-penny/10 border border-penny/20 rounded-2xl p-4 text-center">
              <p className="font-mono text-xs uppercase tracking-widest text-copper mb-1">What I need help with</p>
              <p className="text-foreground/90">{profile.need}</p>
            </div>
          )}
          {!isSupporter && !profile.jar && profile.goal_enabled && profile.goal_amount > 0 && (
            <div data-testid="goal-card" className="mt-4 bg-sage/10 border border-sage/20 rounded-2xl p-4">
              <div className="flex justify-between items-baseline">
                <p className="font-mono text-xs uppercase tracking-widest text-sage">Working toward a goal</p>
                <p className="text-sm font-semibold text-bronze">${(profile.goal_raised || 0).toFixed(2)} / ${profile.goal_amount.toFixed(2)}</p>
              </div>
              {profile.goal_title && <p className="text-foreground/90 mt-1">{profile.goal_title}</p>}
              <div className="h-3 bg-secondary rounded-full mt-2 overflow-hidden">
                <div className="h-full theme-sage rounded-full transition-all" style={{ width: `${Math.min(100, ((profile.goal_raised || 0) / profile.goal_amount) * 100)}%` }} />
              </div>
              {(profile.goal_raised || 0) >= profile.goal_amount
                ? <p className="text-xs text-sage font-semibold mt-1">Goal reached — thank you, everyone! 🎉</p>
                : <p className="text-xs text-muted-foreground mt-1">{Math.round(((profile.goal_raised || 0) / profile.goal_amount) * 100)}% of the way there 💛</p>}
            </div>
          )}
          {!isSupporter && profile.jar && <ProfileJar jar={profile.jar} />}
          {!isSupporter && profile.jar && (
            <div className="mt-3 flex justify-center">
              <ShareJar jar={profile.jar} name={profile.name} goalTitle={profile.goal_title}
                url={typeof window !== "undefined" ? window.location.href : ""} />
            </div>
          )}
          {profile.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {profile.tags.map((t) => <span key={t} className="text-xs font-mono uppercase tracking-wide bg-secondary text-bronze px-2.5 py-1 rounded-full">#{t}</span>)}
            </div>
          )}
        </div>
      </div>

      {!isSupporter && <SupporterWall userId={userId} refreshKey={follow.followers} />}

      <Tabs defaultValue="timeline" className="mt-6">
        <TabsList className="rounded-full bg-secondary p-1 flex-wrap h-auto gap-1">
          <TabsTrigger value="timeline" data-testid="tab-timeline" className="rounded-full">Timeline</TabsTrigger>
          {!isSupporter && <TabsTrigger value="photos" data-testid="tab-photos" className="rounded-full">Photos</TabsTrigger>}
          <TabsTrigger value="about" data-testid="tab-about" className="rounded-full">About</TabsTrigger>
          {isOwn && <TabsTrigger value="transactions" data-testid="tab-transactions" className="rounded-full">Transactions</TabsTrigger>}
        </TabsList>
        <TabsContent value="timeline" className="mt-5 space-y-4">
          {isOwn && <PostComposer onPosted={(p) => setPosts((prev) => [p, ...prev])} />}
          {posts.length === 0 ? (
            <p className="text-center text-muted-foreground py-12">No posts yet.</p>
          ) : posts.map((p) => <PostCard key={p.post_id} post={p} onChange={load} onReport={setReport} />)}
        </TabsContent>
        <TabsContent value="photos" className="mt-5">
          {!isSupporter && <AlbumsTab userId={userId} isOwn={isOwn} />}
        </TabsContent>
        <TabsContent value="about" className="mt-5">
          <div className="bg-card rounded-2xl border border-border p-6">
            <h3 className="font-serif text-xl font-semibold text-bronze mb-2">My story</h3>
            <p className="text-foreground/80 leading-relaxed whitespace-pre-wrap">{profile.story || profile.bio || "No story shared yet."}</p>
          </div>
        </TabsContent>
        {isOwn && (
          <TabsContent value="transactions" className="mt-5">
            <TransactionCenter />
          </TabsContent>
        )}
      </Tabs>

      {editOpen && <EditProfile profile={profile} onClose={() => setEditOpen(false)} onSaved={async () => { await load(); await refreshUser(); setEditOpen(false); }} />}
      <TipModal open={tipOpen} onClose={() => setTipOpen(false)} mode="individual" recipient={profile} />
      <ReportDialog target={report} onClose={() => setReport(null)} />
      {lightbox && <ImageLightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />}

      <Dialog open={verifOpen} onOpenChange={setVerifOpen}>
        <DialogContent data-testid="verify-dialog" className="sm:max-w-md rounded-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-bronze flex items-center gap-2">
              <BadgeCheck className="w-5 h-5 text-sage" /> Request verification
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Upload the documents below to help us verify you. Your files are private and only seen by our review team.
            </p>
            <div className="space-y-2.5">
              {verifSections.map((sec) => {
                const done = verifDocs[sec.id];
                return (
                  <div key={sec.id} data-testid={`verify-section-${sec.id}`} className="rounded-2xl border border-border/70 bg-secondary/30 p-3">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <p className="text-sm font-medium text-bronze">
                        {sec.label} {sec.required && <span className="text-xs text-copper font-semibold">(required)</span>}
                      </p>
                      {done && <span className="text-xs text-sage flex items-center gap-1"><BadgeCheck className="w-3.5 h-3.5" /> uploaded</span>}
                    </div>
                    {done ? (
                      <div className="flex items-center justify-between gap-2 text-xs bg-white rounded-xl px-3 py-2 border border-border/60">
                        <span className="truncate text-foreground/80">{done.name}</span>
                        <button data-testid={`verify-remove-${sec.id}`} onClick={() => setVerifDocs((d) => { const n = { ...d }; delete n[sec.id]; return n; })}
                          className="text-destructive text-xs font-medium shrink-0">Remove</button>
                      </div>
                    ) : (
                      <label className="flex items-center justify-center gap-2 text-sm text-copper bg-white border border-dashed border-copper/40 rounded-xl px-3 py-2.5 cursor-pointer hover:bg-copper/5" data-testid={`verify-upload-${sec.id}`}>
                        {uploadingSec === sec.id ? "Uploading…" : <><Camera className="w-4 h-4" /> Upload file</>}
                        <input type="file" accept="image/*,application/pdf" className="hidden"
                          disabled={uploadingSec === sec.id}
                          onChange={(e) => uploadDoc(sec, e.target.files?.[0])} />
                      </label>
                    )}
                  </div>
                );
              })}
            </div>
            <Textarea data-testid="verify-note" value={verifNote} onChange={(e) => setVerifNote(e.target.value)} rows={3}
              placeholder="Anything you'd like our team to know? (optional)" className="rounded-xl resize-none" />

            <div className="rounded-2xl border border-penny/30 bg-penny/5 p-3 space-y-2">
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" data-testid="verify-want-protected" checked={wantProtected}
                  onChange={(e) => setWantProtected(e.target.checked)} className="mt-1 accent-penny" />
                <span className="text-sm text-bronze font-medium flex items-center gap-1">
                  <Lock className="w-3.5 h-3.5 text-penny" /> I'd like my identity kept private
                  <span className="block text-xs text-muted-foreground font-normal mt-0.5">
                    For personal reasons I'd rather not show my name or face. If our team approves, you'll be placed <b>"Under Our Wing"</b> — fully trusted, but anonymous. Your story can still be shared to help you.
                  </span>
                </span>
              </label>
              {wantProtected && (
                <Textarea data-testid="verify-protected-reason" value={protectedReason} onChange={(e) => setProtectedReason(e.target.value)} rows={3}
                  placeholder="Privately tell our team why you'd like to stay anonymous (only we ever see this)…" className="rounded-xl resize-none bg-white" />
              )}
            </div>

            <p className="text-xs text-muted-foreground">Accepted: images or PDF, up to ~10 MB each.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVerifOpen(false)} className="rounded-full">Cancel</Button>
            <Button data-testid="verify-submit-btn" onClick={submitVerif} className="rounded-full bg-sage hover:bg-sage/90 text-white">
              Send request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EditProfile({ profile, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: profile.name || "", bio: profile.bio || "", story: profile.story || "",
    need: profile.need || "", location: profile.location || "", tags: profile.tags || [],
    theme: profile.theme || "copper", seeking: profile.seeking, avatar_url: profile.avatar_url, cover_url: profile.cover_url,
    gender: profile.gender || "",
    goal_enabled: profile.goal_enabled || false, goal_title: profile.goal_title || "",
    goal_amount: profile.goal_amount || "", show_daily_earnings: profile.show_daily_earnings !== false,
    show_tip_jar: profile.show_tip_jar || false, show_jar_amount: profile.show_jar_amount || false,
    account_type: profile.account_type || "seeker", is_public: profile.is_public !== false,
    accept_dms: profile.accept_dms !== false, auto_thankyou: profile.auto_thankyou || false,
    auto_thankyou_text: profile.auto_thankyou_text || "",
  });
  const [saving, setSaving] = useState(false);
  const [polishing, setPolishing] = useState(false);
  const avatarRef = useRef(); const coverRef = useRef();

  const polishStory = async () => {
    if (!form.story || form.story.trim().length < 8) { toast.error("Write a little of your story first 💛"); return; }
    setPolishing(true);
    try {
      const { data } = await api.post("/ai/polish", { text: form.story, mode: "story" });
      if (data.text) { set("story", data.text); toast.success("Polished with care ✨"); }
    } catch (e) { toast.error(apiErr(e)); }
    setPolishing(false);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleTag = (t) => set("tags", form.tags.includes(t) ? form.tags.filter((x) => x !== t) : [...form.tags, t]);

  const uploadImg = async (e, field) => {
    const f = e.target.files[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set(field, data.url); toast.success("Image uploaded");
    } catch (err) { toast.error(apiErr(err)); }
  };

  const save = async () => {
    setSaving(true);
    try { await api.put("/profiles/me", { ...form, goal_amount: parseFloat(form.goal_amount) || 0 }); toast.success("Profile updated 💛"); onSaved(); }
    catch (e) { toast.error(apiErr(e)); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="edit-profile-dialog" className="sm:max-w-lg rounded-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-2xl text-bronze">Edit your profile</DialogTitle></DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-copper font-mono">Cover & Avatar</Label>
            <div className={`h-24 ${(THEMES[form.theme] || THEMES.copper).cover} rounded-2xl relative mt-1 overflow-hidden`}>
              {form.cover_url && <img src={fileUrl(form.cover_url)} alt="" className="w-full h-full object-cover" />}
              <button onClick={() => coverRef.current?.click()} className="absolute top-2 right-2 bg-black/40 text-white rounded-full p-1.5"><Camera className="w-4 h-4" /></button>
              <img src={fileUrl(form.avatar_url) || `https://api.dicebear.com/7.x/lorelei/svg?seed=${profile.user_id}`} alt="" className="w-16 h-16 rounded-full border-4 border-card object-cover absolute -bottom-3 left-3 bg-secondary" />
              <button onClick={() => avatarRef.current?.click()} className="absolute -bottom-1 left-14 bg-copper text-white rounded-full p-1"><Camera className="w-3 h-3" /></button>
            </div>
            <input ref={avatarRef} data-testid="avatar-upload" type="file" accept="image/*" hidden onChange={(e) => uploadImg(e, "avatar_url")} />
            <input ref={coverRef} data-testid="cover-upload" type="file" accept="image/*" hidden onChange={(e) => uploadImg(e, "cover_url")} />
          </div>

          <div><Label>Name</Label><Input data-testid="edit-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="rounded-xl mt-1" /></div>
          <div><Label>Location</Label><Input data-testid="edit-location" value={form.location} onChange={(e) => set("location", e.target.value)} className="rounded-xl mt-1" placeholder="City, State" /></div>
          <div>
            <Label>Gender <span className="text-muted-foreground font-normal">(helps supporters find you)</span></Label>
            <div className="flex gap-1.5 mt-1.5 flex-wrap">
              {[{ v: "", l: "Prefer not to say" }, { v: "female", l: "Woman" }, { v: "male", l: "Man" }, { v: "nonbinary", l: "Non-binary" }].map((o) => (
                <button key={o.v || "none"} type="button" data-testid={`edit-gender-${o.v || "none"}`} onClick={() => set("gender", o.v)}
                  className={`text-xs font-medium px-3 py-1.5 rounded-full ${form.gender === o.v ? "bg-copper text-white" : "bg-secondary text-bronze"}`}>{o.l}</button>
              ))}
            </div>
          </div>
          <div><Label>What do you need help with?</Label><Input data-testid="edit-need" value={form.need} onChange={(e) => set("need", e.target.value)} className="rounded-xl mt-1" /></div>
          <div>
            <div className="flex items-center justify-between">
              <Label>Your story</Label>
              <button type="button" data-testid="polish-story-btn" onClick={polishStory} disabled={polishing}
                className="inline-flex items-center gap-1 text-xs font-semibold text-copper hover:text-copper/80 disabled:opacity-50">
                <Sparkles className="w-3.5 h-3.5" /> {polishing ? "Polishing…" : "Polish with AI"}
              </button>
            </div>
            <Textarea data-testid="edit-story" value={form.story} onChange={(e) => set("story", e.target.value)} rows={4} className="rounded-xl mt-1" placeholder="Share as much or as little as you like…" />
            <p className="text-xs text-muted-foreground mt-1">Tip: write it however it comes out — tap <b>Polish with AI</b> to gently fix spelling &amp; spacing while keeping your words and your truth.</p>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-copper font-mono">Theme</Label>
            <div className="flex gap-2 mt-2">
              {Object.entries(THEMES).map(([k, v]) => (
                <button key={k} data-testid={`theme-${k}`} onClick={() => set("theme", k)}
                  className={`w-10 h-10 rounded-full ${v.cover} flex items-center justify-center ${form.theme === k ? "ring-2 ring-offset-2 ring-copper" : ""}`}>
                  {form.theme === k && <Check className="w-4 h-4 text-white" />}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-copper font-mono">Tags</Label>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {TAGS.map((t) => (
                <button key={t} onClick={() => toggleTag(t)} data-testid={`edit-tag-${t}`}
                  className={`text-xs font-mono px-2.5 py-1 rounded-full ${form.tags.includes(t) ? "bg-copper text-white" : "bg-secondary text-bronze"}`}>#{t}</button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div><Label>Actively seeking support</Label><p className="text-xs text-muted-foreground">Show my profile on Discovery & the jar split.</p></div>
            <Switch data-testid="edit-seeking" checked={form.seeking} onCheckedChange={(v) => set("seeking", v)} />
          </div>

          <div className="rounded-2xl border border-border p-3 space-y-3">
            <div className="flex items-center justify-between">
              <div><Label>Set a goal (optional)</Label><p className="text-xs text-muted-foreground">Work toward a specific thing, like a new violin.</p></div>
              <Switch data-testid="edit-goal-enabled" checked={form.goal_enabled} onCheckedChange={(v) => set("goal_enabled", v)} />
            </div>
            {form.goal_enabled && (
              <div className="space-y-2">
                <Input data-testid="edit-goal-title" value={form.goal_title} onChange={(e) => set("goal_title", e.target.value)} placeholder="What are you saving toward?" className="rounded-xl" />
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input data-testid="edit-goal-amount" type="number" min="1" value={form.goal_amount} onChange={(e) => set("goal_amount", e.target.value)} placeholder="Goal amount" className="pl-7 rounded-xl" />
                </div>
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-border p-3 space-y-3">
            <p className="text-xs uppercase tracking-widest text-copper font-mono">Privacy & control</p>
            <div className="flex items-center justify-between">
              <div><Label>I'm here to help others</Label><p className="text-xs text-muted-foreground">Turn on for a private supporter account (hidden from browse).</p></div>
              <Switch data-testid="edit-supporter-mode" checked={form.account_type === "supporter"}
                onCheckedChange={(v) => setForm((f) => ({ ...f, account_type: v ? "supporter" : "seeker", seeking: !v, is_public: v ? false : true }))} />
            </div>
            {form.account_type === "supporter" && (
              <div className="flex items-center justify-between">
                <div><Label>Show my profile on the site</Label><p className="text-xs text-muted-foreground">Off = nobody can find or view your profile.</p></div>
                <Switch data-testid="edit-is-public" checked={form.is_public} onCheckedChange={(v) => set("is_public", v)} />
              </div>
            )}
            <div className="flex items-center justify-between">
              <div><Label>Allow private messages</Label><p className="text-xs text-muted-foreground">Let members message you directly.</p></div>
              <Switch data-testid="edit-accept-dms" checked={form.accept_dms} onCheckedChange={(v) => set("accept_dms", v)} />
            </div>
          </div>

          {form.account_type !== "supporter" && (
            <div className="rounded-2xl border border-border p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div><Label>Auto thank-you</Label><p className="text-xs text-muted-foreground">Automatically thank every donor the moment a tip lands.</p></div>
                <Switch data-testid="edit-auto-thankyou" checked={form.auto_thankyou} onCheckedChange={(v) => set("auto_thankyou", v)} />
              </div>
              {form.auto_thankyou && (
                <>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      { k: "Short", t: "Thank you so much — it means the world. 💛" },
                      { k: "Heartfelt", t: "I don't have the words for how much this helps. Your kindness reminds me I'm not alone, and I'll never forget it. Thank you. 💛" },
                      { k: "Funny", t: "You + me = officially friends now. Thank you a latte for the support! ☕💛" },
                    ].map((tpl) => (
                      <button key={tpl.k} type="button" data-testid={`thankyou-tpl-${tpl.k.toLowerCase()}`} onClick={() => set("auto_thankyou_text", tpl.t)}
                        className="text-xs font-medium px-3 py-1.5 rounded-full bg-secondary text-bronze hover:bg-accent">{tpl.k}</button>
                    ))}
                  </div>
                  <Textarea data-testid="edit-auto-thankyou-text" value={form.auto_thankyou_text} onChange={(e) => set("auto_thankyou_text", e.target.value)}
                    rows={2} placeholder="Tap a style above, or write your own (blank = warm default)…" className="rounded-xl" />
                </>
              )}
            </div>
          )}

          <div className="rounded-2xl border border-border p-3 space-y-3">
            <div className="flex items-center justify-between">
              <div><Label>Show a tip jar on my profile</Label><p className="text-xs text-muted-foreground">A warm jar that fills with pennies as support comes in.</p></div>
              <Switch data-testid="edit-show-tipjar" checked={form.show_tip_jar} onCheckedChange={(v) => set("show_tip_jar", v)} />
            </div>
            {form.show_tip_jar && form.goal_enabled && (
              <p className="text-xs text-muted-foreground">Your jar will fill toward your goal and show how close you are.</p>
            )}
            {form.show_tip_jar && !form.goal_enabled && (
              <div className="flex items-center justify-between">
                <div><Label>Show the dollar amount</Label><p className="text-xs text-muted-foreground">Reveal your all-time total. Off keeps the amount private — the jar still fills.</p></div>
                <Switch data-testid="edit-show-jar-amount" checked={form.show_jar_amount} onCheckedChange={(v) => set("show_jar_amount", v)} />
              </div>
            )}
          </div>

          <div className="flex items-center justify-between">
            <div><Label>Show today's earnings on my profile</Label><p className="text-xs text-muted-foreground">Let visitors see how much you've received today.</p></div>
            <Switch data-testid="edit-show-earnings" checked={form.show_daily_earnings} onCheckedChange={(v) => set("show_daily_earnings", v)} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button data-testid="save-profile-btn" onClick={save} disabled={saving} className="bg-copper hover:bg-copper/90 text-white rounded-full">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
